import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Search, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Empty } from "@/components/common";
import { Belt } from "@/components/Belt";
import { useApi, act } from "@/lib/useApi";
import { api } from "@/lib/api";
import { dt, todayISO } from "@/lib/fmt";

function QrDialog({ cls, onClose }) {
  const [qr] = useApi(`/checkin/qr/${cls.id}`);
  const url = qr && `${window.location.origin}/portal/checkin?class=${cls.id}&code=${qr.code}`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md text-center" data-testid="qr-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{cls.name} · {cls.start}</DialogTitle></DialogHeader>
        {qr && <div className="space-y-4">
          <div className="bg-white p-4 inline-block rounded-xl ring-1 ring-slate-200"><QRCodeSVG value={url} size={240} fgColor="#09090B" /></div>
          <div className="text-xs text-slate-500">Aponte a câmera do celular · ou digite o código no app</div>
          <div className="font-mono text-4xl font-bold tracking-[0.3em] text-red-600" data-testid="qr-code-text">{qr.code}</div>
          <div className="text-xs text-slate-400">Válido somente hoje ({dt(qr.date)}) para esta turma</div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export default function CheckIn() {
  const [date, setDate] = useState(todayISO());
  const [data, reload] = useApi(`/checkin/today?d=${date}`);
  const [students] = useApi("/students?status=");
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [qrCls, setQrCls] = useState(null);
  const sessions = data?.sessions || [];
  const classId = cls || sessions[0]?.id;
  const matches = useMemo(() => {
    if (q.length < 2 || !students) return [];
    const k = q.toLowerCase();
    return students.filter((s) => s.full_name.toLowerCase().includes(k)).slice(0, 6);
  }, [q, students]);

  const mark = async (s) => {
    if (!classId) return;
    const r = await act(() => api.post("/checkin/manual", { student_id: s.id, class_id: classId, date }), `Presença registrada: ${s.full_name}`);
    if (r) { setQ(""); reload(); }
  };

  return (
    <div>
      <PageHeader eyebrow="Presença" title="Check-in">
        <Input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="checkin-date-input" />
      </PageHeader>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="panel p-5 space-y-4 h-fit lg:sticky lg:top-6" data-testid="manual-checkin-panel">
          <div className="font-display text-xl font-bold uppercase">Registro rápido</div>
          <div className="flex flex-wrap gap-2">
            {sessions.map((c) => (
              <button key={c.id} onClick={() => setCls(c.id)} data-testid={`select-class-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 transition-colors ${classId === c.id ? "bg-red-600 text-white ring-red-600" : "ring-slate-200 hover:ring-red-300"}`}>
                {c.start} · {c.name}</button>))}
            {!sessions.length && <div className="text-sm text-slate-500">Nenhuma turma neste dia.</div>}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Digite o nome do aluno…" className="pl-9 h-11" data-testid="checkin-search-input" />
          </div>
          <div className="space-y-1">
            {matches.map((s) => (
              <button key={s.id} onClick={() => mark(s)} disabled={!classId} data-testid={`checkin-student-${s.id}`}
                className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-red-50 transition-colors text-left">
                <div><div className="text-sm font-semibold">{s.full_name}</div><div className="text-[11px] uppercase text-slate-500">{s.status}</div></div>
                <div className="flex items-center gap-3"><Belt belt={s.belt} grau={s.grau} size="sm" /><Check className="w-4 h-4 text-red-600" /></div>
              </button>))}
          </div>
          <p className="text-xs text-slate-500">O sistema valida status da matrícula, mensalidades em atraso e impede check-in duplicado no mesmo treino.</p>
        </div>
        <div className="space-y-4">
          <div className="text-sm text-slate-600">Total no dia: <b data-testid="checkin-day-total">{data?.total ?? 0}</b> presenças</div>
          {sessions.map((c) => (
            <div key={c.id} className="panel fade-up" data-testid={`session-${c.id}`}>
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div><div className="font-display text-xl font-bold uppercase">{c.name}</div><div className="text-xs text-slate-500">{c.start}–{c.end} · {c.instructor_name}</div></div>
                <div className="flex items-center gap-3">
                  <div className="font-display text-4xl font-bold text-red-600" data-testid={`session-count-${c.id}`}>{c.count}</div>
                  {date === todayISO() && <Button size="sm" variant="outline" onClick={() => setQrCls(c)} data-testid={`show-qr-${c.id}`}><QrCode className="w-4 h-4 mr-1" />QR Code</Button>}
                </div>
              </div>
              {!c.checkins.length ? <Empty text="Nenhum check-in ainda." /> : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
                  {c.checkins.map((k) => (
                    <div key={k.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2" data-testid={`checkin-${k.id}`}>
                      <div><div className="text-sm font-semibold">{k.student_name}</div><div className="text-[11px] text-slate-500">{k.time} · {k.method}</div></div>
                      <button onClick={async () => { if (window.confirm("Remover este check-in?")) { await act(() => api.delete(`/checkins/${k.id}`), "Removido"); reload(); } }}
                        className="text-slate-400 hover:text-red-600" data-testid={`remove-checkin-${k.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>))}
                </div>)}
            </div>))}
        </div>
      </div>
      {qrCls && <QrDialog cls={qrCls} onClose={() => setQrCls(null)} />}
    </div>
  );
}
